// Renders a "list" report (table of rows) into a standalone, paged HTML document.
//
// Pages are laid out deterministically: every body row has a fixed height
// (cells don't wrap), so rows-per-page is computed, not measured. That makes
// thousands of rows cheap to paginate and lets the preview render only the
// first N pages while the print renders all of them.
//
//   renderList({ template, columns, rows, ctx, maxPages }) → { html, pageCount, rowsPerPage }
//
// columns: [{ key, label, format: 'text'|'number'|'money'|'date', value(row), sum }]
// ctx: { rtl, title, subtitle, company, logo, user, printedAt, labels{...},
//        parties?, summary?, note? }
//   parties: { seller|buyer: { title, lines: [{ label, value }] } } — shown under
//            the header of the first page when the template turns them on
//   summary: [{ label, value, strong }] — totals box after the table (invoices)
//   note:    free text after the summary
//
// Roll paper (receipt printers) has no pages: one continuous sheet whose
// height follows the content.

import { paperSize, DENSITY } from './paper';
import { fontById, fontFaceCss } from './fonts';

var FA = '۰۱۲۳۴۵۶۷۸۹';

function esc(v) {
  return String(v == null ? '' : v)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function digits(str, fa) {
  str = String(str == null ? '' : str);
  return fa ? str.replace(/[0-9]/g, function (d) { return FA[d]; }) : str;
}

function group(n, decimals) {
  var x = Number(n) || 0;
  var parts = Math.abs(x).toFixed(decimals || 0).split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return (x < 0 ? '-' : '') + parts.join('.');
}

function isNumeric(c) {
  return c.format === 'money' || c.format === 'number';
}

export function renderList({ template, columns, rows, ctx, maxPages }) {
  var t = template;
  var th = t.theme;
  var size = paperSize(t.page);
  var rtl = t.direction === 'auto' ? !!ctx.rtl : t.direction === 'rtl';
  var dir = rtl ? 'rtl' : 'ltr';
  var font = fontById(th.fontId);
  var roll = !!size.roll;
  // Report options (toggles the report declares, saved in the template).
  // An entry with `option` (summary row, party line) shows only when it is on.
  var opts = t.options || {};
  var allowed = function (x) {
    return !x.option || !!opts[x.option];
  };
  var radius = Number(th.radius) || 0;

  var fmt = function (c, raw) {
    if (raw == null || raw === '') return '';
    if (isNumeric(c)) return digits(group(raw, c.format === 'money' ? th.decimals : 0), th.faDigits);
    return digits(raw, th.faDigits && c.format !== 'text-latin');
  };
  var money = function (n) {
    return digits(group(n, th.decimals), th.faDigits);
  };

  // ---- columns ----
  var cols = t.columns
    .filter(function (c) { return c.visible; })
    .map(function (c) {
      var def = columns.find(function (d) { return d.key === c.key; });
      if (!def) return null;
      // currency shown as a small second line of the column title
      var unit = def.format === 'money' && opts.currency && ctx.currency ? ctx.currency : '';
      return Object.assign({}, def, { width: c.width || 1, label: c.label || def.label, unit: unit, align: c.align || 'auto' });
    })
    .filter(Boolean);
  if (t.showRowNumber) cols.unshift({ key: '__row', label: ctx.labels.row || '#', width: 0.75, format: 'number', align: 'center' });
  var weight = cols.reduce(function (a, c) { return a + Number(c.width); }, 0) || 1;
  var alignOf = function (c) {
    var a = c.align && c.align !== 'auto' ? c.align : isNumeric(c) ? th.numberAlign : th.cellAlign;
    return 'pm-a-' + a;
  };
  var sumCols = cols.filter(function (c) { return c.sum; });
  var hasSums = sumCols.length > 0;
  var tot = t.totals;
  var parties = t.parties || {};
  var partyList = ['seller', 'buyer'].filter(function (k) {
    return parties[k] && ctx.parties && ctx.parties[k];
  });
  var summaryRows = (ctx.summary || []).filter(allowed);
  var summary = t.showSummary !== false && summaryRows.length ? summaryRows : null;
  var note = t.showNote !== false && ctx.note ? String(ctx.note) : '';
  // Company end text from settings (HTML, e.g. invoice terms) — last thing on the last page.
  var endHtml = ctx.endHtml && opts.endText !== false ? sanitizeHtml(String(ctx.endHtml)) : '';
  // Items as two-line blocks (name, then the numbers) — default on roll paper
  // where table columns are too narrow for amounts.
  var stacked = t.itemLayout === 'stacked' || (t.itemLayout !== 'table' && roll);
  var showTable = t.showTable !== false; // labels: parties only, no item table
  var signLabels = (t.footer.signatureLabels || []).length ? t.footer.signatureLabels : ctx.signatureLabels || [];

  // ---- vertical budget (mm) ----
  var rowH = DENSITY[th.density];
  var inner = size.h - t.page.margins.top - t.page.margins.bottom;
  var headH = t.header.show ? t.header.height + 3 : 0;
  var footH = t.footer.show ? t.footer.height : 0;
  var perPageExtra = hasSums ? (tot.page ? 1 : 0) + (tot.cumulative ? 1 : 0) + (tot.carry ? 1 : 0) : 0;
  var cap = function (first) {
    var body = inner - (first || t.header.repeat ? headH : 0) - footH - rowH * (t.wrapHead ? 2 : 1); // minus thead (2 lines when titles wrap)
    return Math.max(1, Math.floor(body / rowH) - perPageExtra);
  };
  var perFirst = cap(true);
  var perOther = cap(false);
  var partiesH = partyList.length
    ? 6 + 4.6 * (1 + Math.max.apply(null, partyList.map(function (k) { return ctx.parties[k].lines.length; })))
    : 0;
  perFirst = Math.max(1, perFirst - Math.ceil(partiesH / rowH));
  var signOn = !!(t.footer.signatures && signLabels.length);
  // With end text the signatures follow it (see the tail flow below), so they
  // don't reserve space on the last item page.
  var lastReserve = (tot.grand && hasSums ? 1 : 0) + (signOn && !endHtml ? Math.ceil((opts.stampSign !== false && ctx.signatureImages ? 33 : 24) / rowH) : 0) +
    (summary ? Math.ceil((summary.length * 5.5 + 4) / rowH) : 0) + (note ? Math.ceil(12 / rowH) : 0);

  // ---- detail lines under a cell (product code, serials …) ----
  var subOf = function (c, r) {
    if (!c.sub) return [];
    return (c.sub(r, opts) || []).filter(function (x) { return x != null && x !== ''; });
  };
  var subRatio = 0.62; // a detail line is smaller than a row
  // Text in a cell with detail lines (or a `wrap: true` column) wraps instead
  // of being cut with "…". Pages still break by height, so the number of
  // wrapped lines is estimated from the text length and the column width.
  var bodyW = size.w - t.page.margins.left - t.page.margins.right;
  var colMm = function (c) {
    return (Number(c.width) / weight) * bodyW - 3.2; // minus cell padding
  };
  var linesOf = function (text, pt, mm) {
    var len = String(text == null ? '' : text).length;
    var perLine = Math.max(4, Math.floor(mm / (pt * 0.3528 * 0.5)));
    return Math.max(1, Math.ceil(len / perLine));
  };
  var wraps = function (c, r) {
    return !!c.wrap || subOf(c, r).length > 0;
  };
  var rowWeight = function (r) {
    var w = 1;
    cols.forEach(function (c) {
      if (!wraps(c, r)) return;
      var mm = colMm(c);
      var main = linesOf(fmt(c, c.value ? c.value(r) : r[c.key]), th.fontSize, mm); // cellValue isn't defined yet here
      var sub = subOf(c, r).reduce(function (a, x) { return a + linesOf(x, th.fontSize - 1.5, mm); }, 0);
      w = Math.max(w, main * 0.72 + sub * subRatio * 0.9 + 0.28);
    });
    return w > 1 ? Math.ceil(w * 10) / 10 : 1;
  };

  // ---- split rows into pages (by height: rows with detail lines weigh more) ----
  var pages = [];
  var used = [];
  var i = 0;
  if (roll) {
    pages.push(rows.slice());
    i = rows.length;
  }
  while (i < rows.length || pages.length === 0) {
    var capN = pages.length === 0 ? perFirst : perOther;
    var pg = [];
    var load = 0;
    while (i < rows.length) {
      var w = rowWeight(rows[i]);
      if (pg.length && load + w > capN) break;
      pg.push(rows[i]);
      load += w;
      i++;
    }
    pages.push(pg);
    used.push(load);
  }
  var lastCap = pages.length === 1 ? perFirst : perOther;
  if (!roll && lastReserve && (used[used.length - 1] || 0) + lastReserve > lastCap) pages.push([]);
  var rowPages = pages.length;

  // ---- end text + signatures: flows after the last item page onto extra pages ----
  // tail[0] goes under the last item page, tail[1..] are continuation pages.
  // Stamp / signature images per signature box (index → [urls]), switchable by option.
  var signImgs = opts.stampSign !== false && ctx.signatureImages ? ctx.signatureImages : {};
  var signHtml = signOn
    ? '<div class="pm-sign">' + signLabels.map(function (s, i) {
      var im = (signImgs[i] || []).filter(Boolean);
      return '<div' + (im.length ? ' class="pm-sign--img"' : '') + '>' + esc(s) + (im.length
        ? '<span class="pm-sign__img">' + im.map(function (u) { return '<img src="' + esc(u) + '" alt="">'; }).join('') + '</span>'
        : '') + '</div>';
    }).join('') + '</div>'
    : '';
  var tail = [[]];
  if (endHtml && !roll) {
    var endFs = (th.fontSize - 1.5) * 0.3528; // pt -> mm
    var lineMm = endFs * 1.7;
    var widthMm = size.w - t.page.margins.left - t.page.margins.right;
    var perLine = Math.max(10, Math.floor(widthMm / (endFs * 0.5)));
    var blockH = function (b) {
      var txt = b.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/&[a-z#0-9]+;/gi, 'x');
      return Math.max(1, Math.ceil(txt.length / perLine)) * lineMm + 1;
    };
    var blocks = endHtml
      .split(/(?=<(?:p|div|h[1-6]|ul|ol|li|table)[\s>])|(?:<br\s*\/?>)/i)
      .map(function (b) { return b.trim(); })
      .filter(function (b) { return b.replace(/<[^>]*>|&nbsp;|\s/g, '') !== ''; });
    var items = blocks.map(function (b) { return { html: '<div class="pm-end">' + b + '</div>', h: blockH(b) }; });
    if (signOn) items.push({ html: signHtml, h: Object.keys(signImgs).length ? 39 : 30 });
    var lastLoad = (used[rowPages - 1] || 0) + lastReserve;
    var lastCapRows = rowPages === 1 ? perFirst : perOther;
    var free = (lastCapRows - lastLoad) * rowH * 0.92 - 4;
    var contCap = (inner - (t.header.repeat ? headH : 0) - footH) * 0.92;
    items.forEach(function (it) {
      if (it.h > free && tail[tail.length - 1].length + (tail.length === 1 ? 1 : 0) > 0) {
        tail.push([]);
        free = contCap;
      }
      tail[tail.length - 1].push(it.html);
      free -= it.h;
    });
  } else if (endHtml) {
    tail[0].push('<div class="pm-end">' + endHtml + '</div>');
    if (signOn) tail[0].push(signHtml);
  } else if (signOn) tail[0].push(signHtml);
  var pageCount = rowPages + tail.length - 1;

  // ---- pieces ----
  var cellValue = function (c, r, idx) {
    if (c.key === '__row') return idx + 1;
    return c.value ? c.value(r) : r[c.key];
  };
  var sumRow = function (label, sums, cls) {
    var html = '<tr class="' + cls + '">';
    var span = 0;
    var placed = false;
    var flush = function () {
      if (!span) return;
      html += '<td colspan="' + span + '" class="pm-sum-label">' + (placed ? '' : esc(label)) + '</td>';
      placed = true;
      span = 0;
    };
    cols.forEach(function (c) {
      if (c.sum) {
        flush();
        html += '<td class="' + alignOf(c) + ' pm-num">' + esc(money(sums[c.key])) + '</td>';
      } else span++;
    });
    flush();
    return html + '</tr>';
  };
  var colgroup = '<colgroup>' + cols.map(function (c) {
    return '<col style="width:' + ((c.width / weight) * 100).toFixed(2) + '%">';
  }).join('') + '</colgroup>';
  var thead = '<thead><tr>' + cols.map(function (c) {
    return '<th class="' + alignOf(c) + (c.unit ? ' pm-th-unit' : '') + '">' + esc(c.label) +
      (c.unit ? '<small>' + esc(c.unit) + '</small>' : '') + '</th>';
  }).join('') + '</tr></thead>';

  // Header: three slots (start / center / end); each element goes to one.
  var headerHtml = function () {
    var h = t.header;
    var s = h.slots;
    var items = { start: [], center: [], end: [] };
    var put = function (slot, html) {
      if (items[slot]) items[slot].push(html);
    };
    if (ctx.logo) put(s.logo, '<img class="pm-logo" src="' + esc(ctx.logo) + '" alt="">');
    if (ctx.company) put(s.company, '<div class="pm-company">' + esc(ctx.company) + '</div>');
    var sub = h.showSubtitle ? h.subtitle || ctx.subtitle : '';
    put(s.title, '<div class="pm-title">' + esc(h.title || ctx.title) + '</div>' + (sub ? '<div class="pm-sub">' + esc(digits(sub, th.faDigits)) + '</div>' : ''));
    var meta = [];
    (ctx.info || []).forEach(function (l) {
      if (l.value != null && l.value !== '' && allowed(l)) meta.push('<div><span>' + esc(l.label) + ':</span> <b>' + esc(digits(l.value, th.faDigits)) + '</b></div>');
    });
    if (h.showDate) meta.push('<div><span>' + esc(ctx.labels.printedAt) + ':</span> <b>' + esc(digits(ctx.printedAt, th.faDigits)) + '</b></div>');
    if (h.showUser && ctx.user) meta.push('<div><span>' + esc(ctx.labels.user) + ':</span> <b>' + esc(ctx.user) + '</b></div>');
    if (h.showCount) meta.push('<div><span>' + esc(ctx.labels.count) + ':</span> <b>' + esc(money(rows.length).replace(/\.0+$/, '')) + '</b></div>');
    if (meta.length) put(s.meta, '<div class="pm-meta">' + meta.join('') + '</div>');
    return '<div class="pm-head pm-head--' + h.style + '">' +
      ['start', 'center', 'end'].map(function (k) {
        return '<div class="pm-slot pm-slot--' + k + '">' + items[k].join('') + '</div>';
      }).join('') +
      '</div>';
  };
  var footerHtml = function (p) {
    var f = t.footer;
    var items = { start: [], center: [], end: [] };
    var put = function (slot, html) {
      if (items[slot]) items[slot].push(html);
    };
    if (f.text) put(f.slots.text, '<span>' + esc(f.text) + '</span>');
    put(f.slots.date, '<span>' + esc(digits(ctx.printedAt, th.faDigits)) + '</span>');
    put(f.slots.page, '<span>' + esc(digits(ctx.labels.page + ' ' + (p + 1) + ' ' + ctx.labels.of + ' ' + pageCount, th.faDigits)) + '</span>');
    return '<div class="pm-foot pm-foot--' + f.style + '">' +
      ['start', 'center', 'end'].map(function (k) {
        return '<div class="pm-slot pm-slot--' + k + '">' + items[k].join(' · ') + '</div>';
      }).join('') +
      '</div>';
  };

  var partiesHtml = function () {
    return '<div class="pm-parties">' + partyList.map(function (k) {
      var p = ctx.parties[k];
      return '<div class="pm-party pm-party--' + k + '"><div class="pm-party__title">' + esc(p.title) + '</div>' +
        p.lines.filter(function (l) { return (l.keep || (l.value != null && l.value !== '')) && allowed(l); }).map(function (l) {
          var v = l.value == null || l.value === '' ? '' : l.value;
          return '<div class="pm-party__line"><span>' + esc(l.label) + ':</span> <b>' + esc(digits(v, th.faDigits)) + '</b></div>';
        }).join('') + '</div>';
    }).join('') + '</div>';
  };
  // `wide` rows (e.g. amount in words) go on their own full-width line below.
  var summaryHtml = function () {
    var box = summary.filter(function (r) { return !r.wide; });
    var wide = summary.filter(function (r) { return r.wide; });
    return (box.length ? '<div class="pm-summary">' + box.map(function (r) {
      var v = typeof r.value === 'number' ? money(r.value) : digits(r.value, th.faDigits);
      return '<div class="pm-summary__row' + (r.strong ? ' pm-summary__row--strong' : '') + '"><span>' + esc(r.label) +
        '</span><b class="pm-num">' + esc(v) + '</b></div>';
    }).join('') + '</div>' : '') + wide.map(function (r) {
      return '<div class="pm-wide"><span>' + esc(r.label) + ':</span> <b>' + esc(digits(r.value, th.faDigits)) + '</b></div>';
    }).join('');
  };

  // name line: first text column; last line: the other columns as "label value",
  // the last numeric column (row total) bold at the end. Zero amounts are skipped.
  var stackedItem = function (r, idx) {
    var data = cols.filter(function (c) { return c.key !== '__row'; });
    var nameCol = data.find(function (c) { return !isNumeric(c); }) || data[0];
    var totalCol = data.slice().reverse().find(function (c) { return isNumeric(c) && c !== nameCol; });
    var role = function (k) {
      return data.find(function (c) { return c.role === k; });
    };
    var qty = role('qty');
    var unit = role('unit');
    var fee = role('fee');
    var val = function (c) {
      return c ? fmt(c, cellValue(c, r, idx)) : '';
    };
    var calc = '';
    if (qty && fee) {
      calc = '<span class="pm-it__calc"><b class="pm-num">' + esc(val(qty)) + '</b>' + (unit && val(unit) ? ' ' + esc(val(unit)) : '') +
        ' <i>×</i> <b class="pm-num">' + esc(val(fee)) + '</b></span>';
    }
    var parts = data.filter(function (c) {
      return c !== nameCol && c !== totalCol && (!calc || (c !== qty && c !== unit && c !== fee));
    }).map(function (c) {
      var raw = cellValue(c, r, idx);
      if (raw == null || raw === '' || (isNumeric(c) && !Number(raw))) return '';
      return '<span class="pm-it__part"><i>' + esc(c.label) + '</i> <b class="pm-num">' + esc(fmt(c, raw)) + '</b></span>';
    }).filter(Boolean);
    return '<tr class="pm-it"><td><div class="pm-it__name">' +
      (t.showRowNumber ? '<span class="pm-it__no">' + esc(digits(idx + 1, th.faDigits)) + '</span>' : '') +
      esc(fmt(nameCol, cellValue(nameCol, r, idx))) + '</div>' +
      subOf(nameCol, r).map(function (x) { return '<div class="pm-subl">' + esc(digits(x, th.faDigits)) + '</div>'; }).join('') +
      '<div class="pm-it__line"><span class="pm-it__parts">' + (calc || parts.join('')) + '</span>' +
      (totalCol ? '<b class="pm-it__total pm-num">' + esc(fmt(totalCol, cellValue(totalCol, r, idx))) + '</b>' : '') +
      '</div>' + (calc && parts.length ? '<div class="pm-it__extra">' + parts.join('') + '</div>' : '') +
      '</td></tr>';
  };
  var stackedHead = '<thead><tr><th class="pm-a-start">' + esc(ctx.itemsLabel || '') + '</th></tr></thead>';

  // ---- build pages ----
  var running = {};
  sumCols.forEach(function (c) { running[c.key] = 0; });
  var offset = 0;
  var out = [];
  var limit = maxPages ? Math.min(maxPages, pageCount) : pageCount;
  for (var p = 0; p < pageCount; p++) {
    var pr = pages[p] || [];
    var cont = p >= rowPages; // end-text continuation page
    var pageSums = {};
    sumCols.forEach(function (c) { pageSums[c.key] = 0; });
    var body = '';
    if (hasSums && tot.carry && p > 0 && pr.length) body += sumRow(ctx.labels.carry, running, 'pm-row-carry');
    if (stacked) {
      pr.forEach(function (r, k) {
        body += stackedItem(r, offset + k);
      });
    } else pr.forEach(function (r, k) {
      body += '<tr>' + cols.map(function (c) {
        var raw = cellValue(c, r, offset + k);
        if (c.sum) pageSums[c.key] += Number(raw) || 0;
        var txt = fmt(c, raw);
        var subs = subOf(c, r);
        var hs = rowWeight(r) > 1 ? ' style="height:' + (rowWeight(r) * rowH).toFixed(1) + 'mm"' : '';
        return '<td class="' + alignOf(c) + (isNumeric(c) ? ' pm-num' : '') + (wraps(c, r) ? ' pm-wrap' : '') + '"' + hs + '>' + esc(txt) +
          subs.map(function (x) { return '<div class="pm-subl">' + esc(digits(x, th.faDigits)) + '</div>'; }).join('') + '</td>';
      }).join('') + '</tr>';
    });
    offset += pr.length;
    sumCols.forEach(function (c) { running[c.key] += pageSums[c.key]; });
    if (p >= limit) continue; // keep totals running, skip markup beyond the preview
    var isLast = p === rowPages - 1;
    var multi = pageCount > 1;
    if (hasSums && pr.length && multi) {
      if (tot.page) body += sumRow(ctx.labels.pageTotal, pageSums, 'pm-row-page');
      if (tot.cumulative && !(isLast && tot.grand)) body += sumRow(ctx.labels.cumulative, running, 'pm-row-cum');
    }
    if (isLast && hasSums && tot.grand) body += sumRow(ctx.labels.grandTotal, running, 'pm-row-grand');
    var showHead = t.header.show && (p === 0 || t.header.repeat);
    out.push(
      '<section class="pm-page">' +
      (t.watermark ? '<div class="pm-wm">' + esc(t.watermark) + '</div>' : '') +
      (showHead ? headerHtml() : '') +
      (p === 0 && partyList.length ? partiesHtml() : '') +
      '<div class="pm-body">' + (showTable && !cont ? '<div class="pm-tbl"><table' + (stacked ? ' class="pm-stacked"' : '') + '>' +
      (stacked ? (pr.length ? stackedHead : '') : colgroup + (pr.length || isLast ? thead : '')) +
      '<tbody>' + body + '</tbody></table></div>' : '') +
      (isLast && summary ? summaryHtml() : '') +
      (isLast && note ? '<div class="pm-note">' + esc(note) + '</div>' : '') +
      (p >= rowPages - 1 ? (tail[p - rowPages + 1] || []).join('') : '') +
      '</div>' +
      (t.footer.show ? footerHtml(p) : '') +
      '</section>'
    );
  }

  var html = '<!DOCTYPE html><html dir="' + dir + '" lang="' + (rtl ? 'fa' : 'en') + '"><head><meta charset="utf-8"><style>' +
    fontFaceCss(th.fontId) + '\n' + css(t, size, font.family, rtl) + (roll ? '\n' + rollCss(t, size) : '') + '</style></head><body>' + out.join('') + '</body></html>';
  return { html: html, pageCount: pageCount, rowsPerPage: perOther };
}

function css(t, size, family, rtl) {
  var th = t.theme;
  var m = t.page.margins;
  var acc = th.accent;
  var radius = Number(th.radius) || 0;
  var line = th.borders === 'none' ? 'transparent' : th.borderColor;
  var headBg = th.headerStyle === 'solid' ? acc : th.headerStyle === 'soft' ? acc + '1f' : 'transparent';
  var headFg = th.headerStyle === 'solid' ? '#fff' : th.headerStyle === 'soft' ? acc : th.text;
  var start = rtl ? 'right' : 'left';
  var end = rtl ? 'left' : 'right';
  var fs = th.fontSize;
  return [
    '@page{size:' + size.w + 'mm ' + size.h + 'mm;margin:0}',
    '*{box-sizing:border-box}',
    'html,body{margin:0;padding:0;background:#e9edf3}',
    'body{font-family:' + family + ';color:' + th.text + ';font-size:' + fs + 'pt;line-height:1.35;-webkit-print-color-adjust:exact;print-color-adjust:exact;text-rendering:optimizeLegibility}',
    '.pm-page{position:relative;width:' + size.w + 'mm;height:' + size.h + 'mm;padding:' + m.top + 'mm ' + m.right + 'mm ' + m.bottom + 'mm ' + m.left + 'mm;background:#fff;margin:0 auto 8mm;overflow:hidden;display:flex;flex-direction:column}',
    '@media print{html,body{background:#fff}.pm-page{margin:0;break-after:page;page-break-after:always}.pm-page:last-child{break-after:auto;page-break-after:auto}}',
    // alignment helpers (logical)
    '.pm-a-start{text-align:' + start + '}.pm-a-center{text-align:center}.pm-a-end{text-align:' + end + '}',
    '.pm-num{font-variant-numeric:tabular-nums;unicode-bidi:plaintext}',
    // header
    '.pm-head{flex:0 0 auto;min-height:' + t.header.height + 'mm;padding:1.5mm 0 2.5mm;margin-bottom:3mm;display:grid;grid-template-columns:1fr auto 1fr;align-items:center;gap:4mm}',
    '.pm-head--line{border-bottom:0.6mm solid ' + acc + '}',
    '.pm-head--plain{border-bottom:0.2mm solid ' + th.borderColor + '}',
    '.pm-head--band{background:' + acc + ';color:#fff;padding:2mm 4mm;border-radius:' + radius + 'mm}',
    '.pm-head--band .pm-meta span{opacity:.8}',
    '.pm-head--band .pm-title,.pm-head--band .pm-company{color:#fff}',
    '.pm-head--boxed{border:0.3mm solid ' + th.borderColor + ';border-radius:' + radius + 'mm;padding:2mm 4mm;background:' + acc + '0d}',
    '.pm-slot--start .pm-logo + .pm-company,.pm-slot--end .pm-logo + .pm-company{margin-top:1mm}',
    '.pm-slot{display:flex;flex-direction:column;justify-content:center;gap:0.8mm;min-width:0}',
    '.pm-slot--start{align-items:flex-start;text-align:' + start + '}',
    '.pm-slot--center{align-items:center;text-align:center}',
    '.pm-slot--end{align-items:flex-end;text-align:' + end + '}',
    '.pm-logo{max-height:' + Math.max(8, t.header.height - 12) + 'mm;max-width:32mm;object-fit:contain;display:block}',
    '.pm-title{font-size:' + (fs + 5) + 'pt;font-weight:700;color:' + acc + ';white-space:nowrap}',
    '.pm-company{font-size:' + (fs + 1.5) + 'pt;font-weight:700}',
    '.pm-sub{font-size:' + (fs - 1) + 'pt;opacity:.75;max-width:120mm;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}',
    '.pm-meta{font-size:' + (fs - 1) + 'pt;line-height:1.6;white-space:nowrap;min-width:0;max-width:100%}',
    '.pm-meta div{white-space:normal;overflow-wrap:anywhere}',
    '.pm-company{overflow-wrap:anywhere;min-width:0}',
    '.pm-meta span{opacity:.7}.pm-meta b{font-weight:600}',
    // table
    '.pm-body{flex:1 1 auto;min-height:0}',
    'table{width:100%;border-collapse:collapse;table-layout:fixed}',
    'th,td{height:' + DENSITY[th.density] + 'mm;padding:0 1.6mm;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}',
    // wrapHead: long column titles break onto two lines instead of being cut
    t.wrapHead ? '.pm-tbl thead th{white-space:normal;text-overflow:clip;height:auto;min-height:' + DENSITY[th.density] + 'mm;max-height:' + (DENSITY[th.density] * 2) + 'mm;line-height:1.25;padding-top:1mm;padding-bottom:1mm;vertical-align:middle}' : '',
    'th{background:' + headBg + ';color:' + headFg + ';font-weight:700;font-size:' + (fs - 0.5) + 'pt;border-bottom:0.35mm solid ' + (th.headerStyle === 'none' ? th.text : line) + '}',
    'td{border-bottom:0.2mm solid ' + line + '}',
    th.borders === 'grid' ? 'th,td{border:0.2mm solid ' + line + '}' : '',
    th.zebra ? 'tbody tr:nth-child(even) td{background:' + th.zebraColor + '}' : '',
    '.pm-sum-label{font-weight:700}',
    '.pm-row-page td{font-weight:700;background:' + acc + '10 !important;border-top:0.3mm solid ' + acc + '}',
    '.pm-row-cum td{font-weight:600;background:#f1f5f9 !important;color:#334155}',
    '.pm-row-carry td{font-weight:600;font-style:italic;background:#f8fafc !important;color:#475569}',
    '.pm-row-grand td{font-weight:800;background:' + acc + '1f !important;border-top:0.5mm double ' + acc + '}',
    // footer
    '.pm-foot{flex:0 0 auto;height:' + t.footer.height + 'mm;display:grid;grid-template-columns:1fr auto 1fr;align-items:end;gap:4mm;font-size:' + (fs - 1.5) + 'pt;color:#475569;padding-top:1mm}',
    '.pm-foot .pm-slot{flex-direction:row;align-items:flex-end}',
    '.pm-foot .pm-slot--start{justify-content:flex-start}.pm-foot .pm-slot--center{justify-content:center}.pm-foot .pm-slot--end{justify-content:flex-end}',
    '.pm-foot--line{border-top:0.2mm solid ' + th.borderColor + '}',
    '.pm-foot--band{background:' + acc + '14;border-radius:1.2mm;padding:0 3mm;align-items:center}',
    // extras
    '.pm-sign{display:flex;justify-content:space-around;margin-top:8mm;font-size:' + (fs - 0.5) + 'pt}',
    '.pm-sign div{width:28%;text-align:center;padding-bottom:14mm;border-bottom:0.2mm dashed ' + th.borderColor + '}',
    // invoice parts
    '.pm-parties{flex:0 0 auto;display:flex;gap:3mm;margin-bottom:3mm}',
    '.pm-party{flex:1 1 0;min-width:0;border:0.25mm solid ' + th.borderColor + ';border-radius:' + radius + 'mm;overflow:hidden;padding:1.5mm 2.5mm;font-size:' + (fs - 0.5) + 'pt;line-height:1.55}',
    '.pm-party__title{font-weight:700;color:' + acc + ';margin-bottom:0.5mm}',
    '.pm-party__line span{opacity:.7}.pm-party__line b{font-weight:600}',
    '.pm-summary{margin-top:3mm;margin-' + start + ':auto;width:45%;min-width:60mm;border:0.25mm solid ' + th.borderColor + ';border-radius:' + radius + 'mm;overflow:hidden}',
    '.pm-summary__row{display:flex;justify-content:space-between;gap:3mm;padding:1mm 2.5mm;border-bottom:0.2mm solid ' + th.borderColor + '}',
    '.pm-summary__row:last-child{border-bottom:0}',
    '.pm-summary__row--strong{font-weight:800;background:' + acc + '14}',
    '.pm-summary__row span{white-space:nowrap}',
    // label layout: parties one under the other, receiver emphasised
    t.partiesStack ? '.pm-parties{flex-direction:column;gap:2.5mm}.pm-party{flex:0 0 auto}' : '',
    t.partiesStack ? '.pm-party--buyer{font-size:' + (fs + 2.5) + 'pt;line-height:1.6;border-width:0.5mm}.pm-party--buyer .pm-party__title{font-size:' + (fs + 2) + 'pt}' : '',
    '.pm-wide{margin-top:2mm;padding:1.5mm 2.5mm;border:0.25mm dashed ' + th.borderColor + ';border-radius:' + radius + 'mm;font-size:' + fs + 'pt}',
    '.pm-wide span{opacity:.7}',
    '.pm-end + .pm-end{margin-top:0}',
    '.pm-sign div{position:relative}',
    '.pm-sign div.pm-sign--img{padding-bottom:23mm}',
    '.pm-sign__img{position:absolute;left:0;right:0;bottom:1mm;display:flex;justify-content:center;align-items:flex-end;gap:2mm;height:20mm;pointer-events:none}',
    '.pm-sign__img img{max-height:20mm;max-width:45%;object-fit:contain}',
    '.pm-end{margin-top:4mm;font-size:' + (fs - 1.5) + 'pt;text-align:justify;line-height:1.7}',
    '.pm-end p{margin:0 0 1mm}',
    '.pm-note{margin-top:3mm;font-size:' + (fs - 0.5) + 'pt;white-space:pre-wrap;opacity:.85}',
    '.pm-party__title{margin:-1.5mm -2.5mm 1mm;padding:1mm 2.5mm;background:' + acc + '12;border-bottom:0.2mm solid ' + th.borderColor + '}',
    '.pm-summary__row--strong{background:' + acc + ';color:#fff;font-size:' + (fs + 1) + 'pt}',
    '.pm-stacked td{height:auto;padding:1.2mm 1mm;white-space:normal}',
    '.pm-it__name{font-weight:700;display:flex;gap:1.5mm;align-items:baseline;word-break:break-word}',
    '.pm-it__no{opacity:.55;font-weight:600;flex:0 0 auto}',
    '.pm-it__line{display:flex;justify-content:space-between;align-items:flex-end;gap:2mm;margin-top:0.6mm;font-size:' + (fs - 0.5) + 'pt}',
    '.pm-it__parts{display:flex;flex-wrap:wrap;gap:0.4mm 2.5mm;min-width:0}',
    '.pm-it__part{white-space:nowrap}.pm-it__part i{font-style:normal;opacity:.65}',
    '.pm-it__calc i{font-style:normal;opacity:.6;padding:0 0.5mm}',
    '.pm-it__extra{display:flex;flex-wrap:wrap;gap:0.4mm 2.5mm;font-size:' + (fs - 1.5) + 'pt;opacity:.8;margin-top:0.3mm}',
    '.pm-it__total{white-space:nowrap;font-size:' + (fs + 0.5) + 'pt}',
    '.pm-th-unit small{display:block;font-size:' + (fs - 2) + 'pt;font-weight:500;opacity:.8;line-height:1.1}',
    '.pm-th-unit{line-height:1.15}',
    '.pm-subl{font-size:' + (fs - 1.5) + 'pt;line-height:1.35;opacity:.7;white-space:normal;overflow-wrap:anywhere}',
    'td.pm-wrap{white-space:normal;overflow-wrap:anywhere;text-overflow:clip;line-height:1.35;padding-top:0.8mm !important;padding-bottom:0.8mm !important}',
    // rounded table: the wrapper draws the outer frame, cells keep inner lines
    radius ? '.pm-tbl{border:0.25mm solid ' + th.borderColor + ';border-radius:' + radius + 'mm;overflow:hidden}' : '',
    radius ? '.pm-tbl table tr>*:first-child{border-' + start + '-width:0}.pm-tbl table tr>*:last-child{border-' + end + '-width:0}' : '',
    radius ? '.pm-tbl thead tr:first-child th{border-top-width:0}.pm-tbl tbody tr:last-child td{border-bottom-width:0}' : '',
    '.pm-wm{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;pointer-events:none;font-size:60pt;font-weight:800;color:' + acc + ';opacity:.06;transform:rotate(-30deg)}',
  ].join('\n');
}

// Receipt (slip) printer: one continuous sheet, narrow, cells wrap.
function rollCss(t, size) {
  var th = t.theme;
  var fs = th.fontSize;
  var style = th.rollStyle || 'dashed'; // dashed | bold | minimal
  var dash = style === 'minimal' ? '0.2mm solid #d1d5db' : '0.3mm dashed #000';
  var out = [
    '@page{size:' + size.w + 'mm auto;margin:0}',
    'html,body{background:#fff}',
    '.pm-page{height:auto;min-height:0;overflow:visible;margin:0 auto}',
    '.pm-head{height:auto;min-height:0;display:flex;flex-direction:column;align-items:center;text-align:center;gap:1mm;padding:1mm 0 2mm;border-bottom:' + dash + '}',
    '.pm-head .pm-slot{align-items:center;text-align:center}',
    '.pm-logo{max-height:14mm}',
    '.pm-title,.pm-meta,.pm-sub{white-space:normal}',
    '.pm-meta{display:flex;flex-wrap:wrap;justify-content:center;gap:0 3mm}',
    '.pm-parties{flex-direction:column;gap:1.5mm;margin-bottom:2mm}',
    '.pm-party{flex:0 0 auto;border:0;border-bottom:' + dash + ';border-radius:0;padding:0 0 1.5mm}',
    '.pm-party__title{margin:0 0 0.5mm;padding:0;background:transparent;border:0}',
    '.pm-tbl{border:0;border-radius:0}',
    'th,td{height:auto;padding:0.8mm 0;white-space:normal;overflow:visible;word-break:break-word}',
    '.pm-stacked td{padding:1.2mm 0;border-bottom:' + (style === 'minimal' ? '0.2mm solid #e5e7eb' : '0.2mm dotted #6b7280') + '}',
    '.pm-stacked th{border-bottom:' + dash + '}',
    '.pm-summary{width:100%;min-width:0;border:0;border-radius:0;margin-top:2mm}',
    '.pm-summary__row{padding:0.6mm 0;border:0}',
    '.pm-summary__row--strong{background:transparent;color:inherit;border-top:' + dash + ';margin-top:1mm;padding-top:1.5mm;font-size:' + (fs + 2) + 'pt}',
    '.pm-note{text-align:center;border-top:' + dash + ';padding-top:2mm}',
    '.pm-foot{height:auto;padding-top:2mm;border:0}',
  ];
  if (style === 'bold') {
    out.push(
      '.pm-title{background:#000;color:#fff !important;padding:0.8mm 4mm;border-radius:1mm}',
      '.pm-summary__row--strong{background:#000;color:#fff;border:0;border-radius:1mm;padding:1.5mm 2mm;font-size:' + (fs + 2.5) + 'pt}',
      '.pm-it__total{font-size:' + (fs + 1) + 'pt}'
    );
  }
  if (style === 'minimal') {
    out.push('.pm-title{font-size:' + (fs + 3) + 'pt}', '.pm-company{font-size:' + (fs + 2) + 'pt}');
  }
  return out.join('\n');
}

// Settings text is the tenant's own HTML; drop scripts, handlers and js: links.
function sanitizeHtml(html) {
  return html
    .replace(/<\s*(script|style|iframe|object|embed)[\s\S]*?<\s*\/\s*\1\s*>/gi, '')
    .replace(/<\s*(script|iframe|object|embed)[^>]*>/gi, '')
    .replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '')
    .replace(/(href|src)\s*=\s*("|')?\s*javascript:[^"'>\s]*\2?/gi, '$1="#"');
}
